import { useContext, useState } from 'react';
import { Context as PlannerItemContext } from '@context/PlannerItemContext';
import { submitSubscriptionFeedback } from '@api/fetchers/auth';
import H6 from '@base/H6/H6';
import P2 from '@base/P2/P2';
import RateYourExperienceDetails from '@comp/RateYourExperience/RateYourExperienceDetails';
import {
  PLUS_HOME_FEEDBACK_SUBMITTED,
  PLANNER_CARD_CLICKED,
  PLANNER_PAGE
} from '@constants/segment';
import styled from '@emotion/styled';
import { Spacings } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { getImage } from '@utils/imageHelper';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import useFetch from '@hooks/useFetch';
import {
  browseTopSpacing,
  dayTitleLeftSpacing,
  plannerItemTypeHeightMap,
  PLUS_SUBSCRIPTION
} from '../../planner-constants';
import SubscriptionRatingStar from './SubscriptionRatingStar';

const OuterWrapper = styled.div`
  padding-top: ${browseTopSpacing}px;
  height: ${({ $height }) => $height}px;
`;

const Wrapper = styled.div`
  margin-left: ${dayTitleLeftSpacing};
  background: url('${getImage('planner/feedback.svg')}'),
    linear-gradient(0deg, var(--color-i-orange-8), var(--color-i-orange-8)),
    var(--color-base-1);
  background-repeat: no-repeat;
  background-position: bottom right;
  box-shadow: ${boxShadowGenerator()};
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_8B} ${Spacings.SPACING_6B};
  height: 100%;
`;

const ContentWrapper = styled.div`
  width: 72%;
`;

const StyledRating = styled(SubscriptionRatingStar)`
  align-items: center;
  margin-top: ${Spacings.SPACING_4B};
`;

const brownColor = 'var(--color-i-brown-4)';

const Title = styled(H6)`
  line-height: 129%;
`;

const Description = styled(P2)`
  line-height: 133%;
  margin-top: ${Spacings.SPACING_1B};
`;

const SubscriptionFeedbackCard = ({ metaItem }) => {
  const { refreshFeatureCards } = useContext(PlannerItemContext);
  const goalUID = useSelectedGoalUID();
  const { data: feedbackOptions } = useFetch(
    `v1/feedback/${PLUS_SUBSCRIPTION}/?object_uid=${goalUID}`
  );
  const goalSegmentData = useGoalEventProps({ goalUID });

  const [ratingValue, setRatingValue] = useState(0);
  const { plannerItemType } = metaItem;
  const height = plannerItemTypeHeightMap[plannerItemType];
  const [drawerState, setDrawerState] = useState(false);

  const handleClick = (value) => () => {
    setRatingValue(value);
    setDrawerState(true);
    segment.track(PLANNER_CARD_CLICKED, {
      ...goalSegmentData,
      last_primary_source: PLANNER_PAGE,
      last_primary_source_section: 'Planner',
      card_type: 'Plus Subscription Rating'
    });
  };

  const handleCloseRatingDialog = (closeData) => {
    setRatingValue(0);
    const hasSubmittedFeedback = closeData?.hasSubmittedFeedback;
    if (hasSubmittedFeedback) {
      refreshFeatureCards().then(() => {
        setDrawerState(false);
      });
    } else {
      setDrawerState(false);
    }
  };

  return (
    <OuterWrapper $height={height}>
      <RateYourExperienceDetails
        ratingValue={ratingValue}
        setRatingValue={setRatingValue}
        feedBackDetails={feedbackOptions}
        submitFeedback={submitSubscriptionFeedback}
        objectUID={goalUID}
        extraData={{ goalUID, feedbackType: PLUS_SUBSCRIPTION }}
        drawerState={drawerState}
        onClose={handleCloseRatingDialog}
        source={PLUS_HOME_FEEDBACK_SUBMITTED}
        goalUID={goalUID}
      />
      <Wrapper>
        <ContentWrapper>
          <Title color={brownColor}>
            Give us your feedback on learning with Unacademy
          </Title>
          <Description color={brownColor}>
            Your word will help make Unacademy better
          </Description>
          <StyledRating value={ratingValue} setRating={handleClick} />
        </ContentWrapper>
      </Wrapper>
    </OuterWrapper>
  );
};

export default SubscriptionFeedbackCard;
